import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { format, subYears } from 'date-fns';
import { SzamlazzhuService } from 'src/app/services/szamlazzhu/szamlazzhu.service';
import {
  GetKsziStatusResponse,
  SzamlazzhuClientItem,
} from 'src/app/services/szamlazzhu/types';
import { UNKNOWN_CLIENT_NAME } from 'src/app/shared/payee-cases/payee-cases.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeDatepickerComponent } from '../../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-kszi-info',
  templateUrl: './kszi-info.component.html',
  styleUrls: ['./kszi-info.component.scss'],
  imports: [
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeDatepickerComponent,
    PayeeButtonComponent,
    DatePipe,
  ],
})
export class KsziInfoComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private szamlazzhuService = inject(SzamlazzhuService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private readonly destroy = new Subject<void>();

  readonly client: FormControl<SzamlazzhuClientItem | null>;
  readonly kszi_from: FormControl<Date | null>;
  readonly kszi_from_max = new Date(Date.now() - 24 * 60 * 60 * 1000);

  get canEnableKszi(): boolean {
    return this.kszi_from.valid && this.client.valid;
  }

  ksziStatus?: GetKsziStatusResponse;

  clients: SzamlazzhuClientItem[] = [];
  readonly setClientSubject = new Subject<string>();

  constructor() {
    this.client = this.fb.control<SzamlazzhuClientItem | null>(
      null,
      Validators.required,
    );
    const ksziFrom = subYears(new Date(), 2);
    this.kszi_from = this.fb.control<Date | null>(ksziFrom, [
      Validators.required,
      this.dateMax(this.kszi_from_max),
    ]);
  }

  ngOnInit(): void {
    this.setClientSubject
      .pipe(takeUntil(this.destroy), debounceTime(500), distinctUntilChanged())
      .subscribe({
        next: async (search) => await this.setClients(search),
      });

    this.client.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(async (client) => {
        if (!client) {
          return;
        }

        await this.setClient(client.uuid);
      });

    const client_uuid = this.route.snapshot.queryParams.client_uuid;
    if (typeof client_uuid === 'string' && client_uuid) {
      this.client.patchValue({
        uuid: client_uuid,
        name: UNKNOWN_CLIENT_NAME,
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  clientDisplayWith(item?: SzamlazzhuClientItem): string {
    return item?.name ?? '';
  }

  async enableKszi(): Promise<void> {
    if (!this.canEnableKszi || !this.kszi_from.value) {
      return;
    }

    const value = this.client.value;
    if (!value) {
      return;
    }

    try {
      await this.szamlazzhuService.toggleKszi(this.client.value.uuid, {
        kszi_from: format(this.kszi_from.value, 'yyyy-MM-dd'),
        switch: 1,
      });

      await this.setClient(this.client.value.uuid);
    } catch (error) {
      console.error('Error while enabling kszi', error);
    }
  }

  private async setClients(search: string): Promise<void> {
    const result = await this.szamlazzhuService.getSzamlazzhuClients({
      search,
    });
    this.clients = result.clients;
  }

  private dateMax(max: Date): ValidatorFn {
    return (control) => {
      const value = control.value as Date | null;
      if (!(value instanceof Date)) {
        return null;
      }

      if (value.getTime() <= max.getTime()) {
        return null;
      }

      return {
        max: true,
      };
    };
  }

  private async setClient(uuid: string): Promise<void> {
    try {
      this.router.navigate([], {
        queryParams: {
          client_uuid: uuid,
        },
        queryParamsHandling: 'merge',
      });
      this.ksziStatus = await this.szamlazzhuService.getKsziStatus(uuid);

      // If page was refreshed we only know the uuid
      this.client.patchValue(
        {
          name: this.ksziStatus.client.name ?? UNKNOWN_CLIENT_NAME,
          uuid: this.ksziStatus.client.uuid,
        },
        {
          emitEvent: false,
        },
      );
    } catch (error) {
      console.error('Error while getting kszi status', error);
    }
  }
}
